import React, { useMemo, useRef } from 'react';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Autocomplete, Box, FilterOptionsState, TextField, Typography } from '@mui/material';

import { useAuth } from 'contexts/AuthContext';

import { FinancialYearResult } from 'stores/FinancialYearStoreAdapter';

// --- 型定義 ---

type FinancialYearOption = {
  /** 年度 */
  financialYear: number;
  /** 期間開始日 */
  startDate: string;
  /** 期間終了日 */
  endDate: string;
  /** 表示ラベル */
  label: string;
};

type FinancialYearSelectProps = {
  /** 選択中の値（年度数値文字列） */
  value: string;
  /** 値変更ハンドラ */
  onChange: (value: string) => void;
  /** ラベル */
  label?: string;
  /** 無効状態 */
  disabled?: boolean;
  /** エラー表示 */
  error?: boolean;
  /** エラーメッセージ */
  helperText?: string;
  /** 必須表示 */
  required?: boolean;
};

/**
 * 年度選択コンポーネント（Autocomplete ベース）
 *
 * financialYearData は AuthContext から自動取得する。
 * 初回プルダウンopen時にvalueが未設定の場合、currentの年度を初期値として設定する。
 * ListItem: main=XX期, sub=最初のmonth.start〜最後のmonth.end
 */
const FinancialYearSelect: React.FC<FinancialYearSelectProps> = ({
  value,
  onChange,
  label,
  disabled = false,
  error = false,
  helperText,
  required = false,
}) => {
  const { financialYearData } = useAuth();
  const hasOpenedRef = useRef(false);

  const options: FinancialYearOption[] = useMemo(() => {
    return financialYearData.results.map((fy: FinancialYearResult) => {
      const months = fy.months;
      const startDate = months.length > 0 ? months[0].startDate : '';
      const endDate = months.length > 0 ? months[months.length - 1].endDate : '';
      return {
        financialYear: fy.financialYear,
        startDate,
        endDate,
        label: `${fy.financialYear}期`,
      };
    });
  }, [financialYearData.results]);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    return options.find((o) => String(o.financialYear) === value) || null;
  }, [options, value]);

  const handleOpen = () => {
    if (!hasOpenedRef.current && !value && financialYearData.current && !disabled) {
      hasOpenedRef.current = true;
      onChange(String(financialYearData.current.financialYear));
    }
    hasOpenedRef.current = true;
  };

  const handleChange = (_: unknown, newValue: FinancialYearOption | null) => {
    if (newValue) {
      onChange(String(newValue.financialYear));
    } else {
      onChange('');
    }
  };

  const filterOptions = (
    opts: FinancialYearOption[],
    state: FilterOptionsState<FinancialYearOption>,
  ) => {
    const input = state.inputValue.toLowerCase();
    if (!input) return opts;
    return opts.filter(
      (o) => o.label.toLowerCase().includes(input) || String(o.financialYear).includes(input),
    );
  };

  const renderOption = (
    optionProps: React.HTMLAttributes<HTMLLIElement> & { key: React.Key },
    option: FinancialYearOption,
  ) => {
    const { key, ...rest } = optionProps;
    const startLabel = option.startDate ? option.startDate.substring(0, 10) : '';
    const endLabel = option.endDate ? option.endDate.substring(0, 10) : '';
    return (
      <Box
        component="li"
        key={String(key)}
        {...rest}
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}
      >
        <CalendarMonthIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500} noWrap>
            {option.financialYear}期
          </Typography>
          {startLabel && endLabel && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {startLabel}〜{endLabel}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Autocomplete
      options={options}
      value={selectedOption}
      onChange={handleChange}
      onOpen={handleOpen}
      getOptionLabel={(option) => option.label}
      filterOptions={filterOptions}
      isOptionEqualToValue={(option, val) => option.financialYear === val.financialYear}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          required={required}
          error={error}
          helperText={helperText}
          placeholder="年度を検索..."
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      )}
      renderOption={renderOption}
      noOptionsText="該当する年度がありません"
      sx={{ width: '100%' }}
    />
  );
};

export default FinancialYearSelect;
